import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axiosClient";
import HireSidebar           from '../../components/client/HireSidebar';
import { LogoMark } from '../../components/client/kzIcons';
import { AVAILABILITY_CSS } from '../../styles/availabilityCSS';
import NotificationsTab      from '../../components/client/tabs/NotificationsTab';
import SupportTab            from '../../components/client/tabs/SupportTab';
import MessagesTab           from '../../components/client/tabs/MessagesTab';
import InvoicesTab           from '../../components/client/tabs/InvoicesTab';
import ClientDashboardTab    from '../../components/client/tabs/ClientDashboardTab';
import ProfileTab            from '../../components/client/tabs/ProfileTab';
import RequestServiceTab     from '../../components/client/tabs/RequestServiceTab';
import DispatchTrackerTab    from '../../components/client/tabs/DispatchTrackerTab';

const API_BASE   = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api';
const MEDIA_BASE = (process.env.REACT_APP_MEDIA_URL || process.env.REACT_APP_API_BASE_URL || '').replace(/\/api\/?$/, '');

const imgUrl = (p) => {
  if (!p) return null;
  if (p.startsWith('http://') || p.startsWith('https://')) return p;
  return `${MEDIA_BASE}${p.startsWith('/') ? '' : '/'}${p}`;
};
const avi = (name) => `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'C')}&background=0c0c0b&color=fff000&size=80&bold=true`;
const initials = (name) => (name || 'C').trim().split(/\s+/).slice(0, 2).map(w => w[0]?.toUpperCase() || '').join('') || 'C';
const getJobCraftsmanName = (job) => {
  if (!job) return '—';
  const c = job.craftsman;
  if (c && typeof c === 'object') {
    const name = c.full_name || c.name || c.user?.full_name || c.user?.username || null;
    if (name) return name;
  }
  return job.craftsman_name || job.craftsman_full_name || '—';
};

const SERVICES  = ["Plumbing","Electrical","Carpentry","Painting","Masonry","Tiling","Roofing","Tailoring","Metalwork","Other"];
const LOCATIONS = ["Nairobi","Mombasa","Kisumu","Eldoret","Nakuru","Thika","Kisii","Nyeri","Meru","Machakos"];

const TITLES = {
  dashboard: 'Dashboard', request: 'Request a service', track: 'My jobs',
  messages: 'Messages', notifications: 'Notifications',
  invoices: 'Invoices', support: 'Support', profile: 'Profile',
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Inter:wght@300;400;500;600;700;800;900&display=swap');
@import url('https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.4.0/css/all.min.css');

.hcp {
  --sb-w: 244px;
  --yellow: #fff000; --yellow-d: #fff000; --yellow-l: #fffbc7;
  --green: #16a34a; --green-d: #15803d; --green-l: #f0fdf4;
  --black: #000000; --off: #fcfcfd; --border: #ececee;
  --text: #0f0f10; --muted: #6b7280;
  all: initial; font-family: 'Inter', sans-serif;
  display: block; background: var(--off); color: var(--text); min-height: 100vh;
}
.hcp *, .hcp *::before, .hcp *::after { box-sizing: border-box; margin: 0; padding: 0; }

@keyframes hcp-spin    { to { transform: rotate(360deg); } }
@keyframes hcp-fadeUp  { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
@keyframes hcp-pulse   { 0%,100% { opacity:1; } 50% { opacity:.45; } }
@keyframes hcp-glow    { 0%,100% { box-shadow:0 0 0 0 rgba(34,197,94,.3); } 50% { box-shadow:0 0 0 8px rgba(34,197,94,0); } }

.hcp .shell { display: flex; min-height: 100vh; }
.hcp .col { flex: 1; min-width: 0; display: flex; flex-direction: column; }

/* ── sidebar shell (content lives in HireSidebar) ── */
.hcp .sb { width: var(--sb-w); min-width: var(--sb-w); background: #000; flex-shrink: 0; align-self: stretch; position: relative; z-index: 10; }
.hcp .sb-sticky { position: sticky; top: 0; height: 100vh; overflow: hidden; }

/* ── desktop top bar ── */
.hcp .dtop { position: sticky; top: 0; z-index: 200; height: 68px; background: #fff; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: flex-end; gap: 22px; padding: 0 32px; flex-shrink: 0; }
.hcp .dtop-bell { position: relative; width: 40px; height: 40px; border: 0; background: none; border-radius: 10px; color: #111; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: background .15s; }
.hcp .dtop-bell:hover { background: #f4f4f6; }
.hcp .dtop-bell:focus-visible, .hcp .dtop-user:focus-visible { outline: 2px solid #000; outline-offset: 2px; }
.hcp .dtop-badge { position: absolute; top: 2px; right: 1px; min-width: 18px; height: 18px; padding: 0 5px; border-radius: 9px; background: var(--yellow); border: 2px solid #fff; color: #000; font-size: .62rem; font-weight: 800; display: flex; align-items: center; justify-content: center; line-height: 1; }
.hcp .dtop-userwrap { position: relative; }
.hcp .dtop-user { display: flex; align-items: center; gap: 12px; padding: 4px 6px 4px 4px; border: 0; background: none; border-radius: 30px; cursor: pointer; color: #111; font-family: inherit; }
.hcp .dtop-user:hover { background: #f4f4f6; }
.hcp .dtop-av { width: 40px; height: 40px; border-radius: 50%; background: #000; color: var(--yellow); font-weight: 800; font-size: .9rem; display: flex; align-items: center; justify-content: center; letter-spacing: .02em; }
.hcp .dtop-name { font-size: .92rem; font-weight: 500; }
.hcp .dtop-menu { position: absolute; right: 0; top: calc(100% + 8px); min-width: 210px; padding: 6px; background: #fff; border: 1px solid var(--border); border-radius: 12px; box-shadow: 0 12px 36px rgba(0,0,0,.14); z-index: 300; animation: hcp-fadeUp .14s ease both; }
.hcp .dtop-menu button { display: flex; align-items: center; gap: 11px; width: 100%; padding: 10px 12px; border: 0; background: none; border-radius: 8px; font-family: inherit; font-size: .84rem; font-weight: 500; color: #1b1b1d; cursor: pointer; text-align: left; }
.hcp .dtop-menu button:hover { background: #f4f4f6; }
.hcp .dtop-menu hr { border: 0; border-top: 1px solid var(--border); margin: 5px 0; }
.hcp .dtop-menu .out { color: #b91c1c; }

/* ── mobile chrome ── */
.hcp .topbar { display: none; position: fixed; top: 0; left: 0; right: 0; height: 56px; background: #000000; z-index: 300; align-items: center; padding: 0 16px; gap: 12px; border-bottom: 1px solid rgba(255,240,0,.12); }
.hcp .topbar-brand { display: flex; align-items: center; gap: 8px; font-size: 1.05rem; font-weight: 800; color: #fff; flex: 1; letter-spacing: -.02em; }
.hcp .hamburger { width: 38px; height: 38px; border-radius: 9px; background: rgba(255,240,0,.12); color: #fff000; border: 1.5px solid rgba(255,240,0,.25); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: .88rem; flex-shrink: 0; }

.hcp .overlay { display: none; position: fixed; inset: 0; background: rgba(0,0,0,.65); z-index: 400; }
.hcp .overlay.show { display: block; }

.hcp .drawer { position: fixed; top: 0; left: 0; bottom: 0; width: 280px; max-width: 86vw; z-index: 500; background: #000000; display: flex; flex-direction: column; overflow-y: auto; overscroll-behavior: contain; transform: translateX(-100%); transition: transform .3s cubic-bezier(.4,0,.2,1); pointer-events: none; }
.hcp .drawer.open { transform: translateX(0); pointer-events: auto; }
.hcp .drawer > .hs { min-height: 100%; height: auto; }
.hcp .drawer-close { position: absolute; top: 18px; right: 14px; z-index: 2; background: rgba(255,255,255,.12); border: none; border-radius: 8px; width: 32px; height: 32px; color: rgba(255,255,255,.7); display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: .85rem; transition: background .15s; }
.hcp .drawer-close:hover { background: rgba(255,255,255,.22); color: #fff; }

.hcp .main { flex: 1; min-width: 0; padding: 28px 32px 56px; background: var(--off); }

/* ── cards ── */
.hcp .card { background: #fff; border-radius: 14px; box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 6px 18px rgba(16,24,40,.04); padding: 1.75rem; margin-bottom: 1.5rem; border: 1px solid var(--border); position: relative; overflow: hidden; animation: hcp-fadeUp .3s ease both; }

.hcp .filter-bar { display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 22px; align-items: center; }
.hcp .srch-wrap { flex: 1; min-width: 180px; position: relative; }
.hcp .srch-ico { position: absolute; left: 13px; top: 50%; transform: translateY(-50%); color: var(--muted); font-size: .82rem; pointer-events: none; }
.hcp .srch { width: 100%; padding: 11px 13px 11px 36px; font-size: .88rem; border: 1.5px solid var(--border); border-radius: 10px; background: #fff; color: var(--text); font-family: 'Inter', sans-serif; outline: none; transition: border-color .2s, box-shadow .2s; font-weight: 500; }
.hcp .srch:focus { border-color: #000; box-shadow: 0 0 0 4px rgba(255,240,0,.35); }
.hcp .filter-sel { padding: 11px 15px; border: 1.5px solid var(--border); border-radius: 10px; font-size: .88rem; font-family: 'Inter', sans-serif; outline: none; background: #fff; color: var(--text); cursor: pointer; min-width: 145px; font-weight: 600; transition: border-color .2s; }
.hcp .filter-sel:focus { border-color: #000; }

.hcp .req-filter-bar { display: flex; gap: 8px; flex-wrap: wrap; padding: 14px 20px 0; }
.hcp .req-filter-pill { display: inline-flex; align-items: center; gap: 6px; background: #fff; border: 1.5px solid var(--border); color: var(--muted); border-radius: 50px; padding: 6px 13px; font-size: .76rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; }
.hcp .req-filter-pill:hover { border-color: #d6d6dc; background: #fafafa; }
.hcp .req-filter-pill.on { background: #000000; border-color: #000000; color: #fff000; }
.hcp .req-filter-count { font-size: .68rem; font-weight: 800; opacity: .7; }
.hcp .req-filter-pill.on .req-filter-count { opacity: .85; }

.hcp .craft-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(230px, 1fr)); gap: 18px; }
.hcp .craft-grid-item { display: flex; flex-direction: column; gap: 8px; }
.hcp .save-toggle-btn { display: flex; align-items: center; justify-content: center; gap: 6px; background: #fff; border: 1.5px solid var(--border); color: var(--muted); border-radius: 10px; padding: 8px 0; font-weight: 700; font-size: .76rem; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .18s; }
.hcp .save-toggle-btn:hover { border-color: #fca5a5; color: #b91c1c; background: #fef2f2; }
.hcp .save-toggle-btn.saved { border-color: #fca5a5; color: #b91c1c; background: #fef2f2; }
.hcp .save-toggle-btn.saved i { color: #ef4444; }
.hcp .craft-card { background: #fff; border: 1px solid var(--border); border-radius: 14px; overflow: hidden; display: flex; flex-direction: column; flex: 1; transition: transform .22s, box-shadow .22s, border-color .2s; box-shadow: 0 1px 2px rgba(16,24,40,.04); }
.hcp .craft-card:hover { transform: translateY(-3px); box-shadow: 0 14px 34px rgba(0,0,0,.1); border-color: #fff000; }
.hcp .craft-cover { position: relative; height: 140px; overflow: hidden; flex-shrink: 0; }
.hcp .craft-cover img { width: 100%; height: 100%; object-fit: cover; transition: transform .38s; display: block; }
.hcp .craft-card:hover .craft-cover img { transform: scale(1.07); }
.hcp .craft-cover::after { content: ''; position: absolute; inset: 0; background: linear-gradient(to top, rgba(0,0,0,.5) 0%, transparent 55%); }
.hcp .trade-pill { position: absolute; top: 10px; left: 10px; z-index: 1; background: #fff000; color: #000000; border-radius: 50px; padding: 3px 11px; font-size: .6rem; font-weight: 800; letter-spacing: .04em; box-shadow: 0 2px 8px rgba(255,240,0,.4); }

/* ── Availability indicators ── */
.hcp .avail-pill { position: absolute; top: 10px; right: 10px; z-index: 1; background: #16a34a; color: #fff; border-radius: 50px; padding: 3px 9px; font-size: .58rem; font-weight: 700; display: flex; align-items: center; gap: 4px; box-shadow: 0 2px 8px rgba(22,163,74,.4); }
.hcp .avail-pill-busy { position: absolute; top: 10px; right: 10px; z-index: 1; background: #f59e0b; color: #fff; border-radius: 50px; padding: 3px 9px; font-size: .58rem; font-weight: 700; display: flex; align-items: center; gap: 4px; box-shadow: 0 2px 8px rgba(245,158,11,.4); }
.hcp .avail-dot { width: 5px; height: 5px; border-radius: 50%; background: #fff; animation: hcp-pulse 1.5s infinite; }
.hcp .avail-status-bar { font-size: .72rem; font-weight: 700; padding: 7px 10px; border-radius: 9px; text-align: center; margin-top: 2px; }
.hcp .avail-status-bar.online { background: var(--green-l); color: var(--green-d); border: 1.5px solid #bbf7d0; }
.hcp .avail-status-bar.busy   { background: #fffbeb; color: #b45309; border: 1.5px solid #fde68a; }
.hcp .notify-btn { width: 100%; padding: 10px 0; background: #fff; border: 2px solid #fbbf24; color: #b45309; border-radius: 11px; font-weight: 800; font-size: .82rem; cursor: pointer; font-family: 'Inter', sans-serif; display: flex; align-items: center; justify-content: center; gap: 7px; margin-top: auto; transition: all .2s; }
.hcp .notify-btn:hover { background: #fffbeb; border-color: #f59e0b; transform: translateY(-1px); }
.hcp .notify-sent { width: 100%; padding: 10px 4px; background: var(--green-l); border: 2px solid #bbf7d0; color: var(--green-d); border-radius: 11px; font-weight: 700; font-size: .76rem; display: flex; align-items: center; justify-content: center; gap: 7px; text-align: center; margin-top: auto; line-height: 1.35; }

.hcp .craft-body { padding: 15px; display: flex; flex-direction: column; gap: 8px; flex: 1; }
.hcp .craft-row { display: flex; align-items: center; gap: 10px; }
.hcp .craft-av { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 2.5px solid #fff000; flex-shrink: 0; }
.hcp .craft-name { font-weight: 700; font-size: .9rem; color: var(--text); }
.hcp .craft-loc { font-size: .72rem; color: #16a34a; font-weight: 600; margin-top: 2px; }
.hcp .craft-stars { display: inline-flex; gap: 1px; font-size: .68rem; color: #f59e0b; }
.hcp .craft-rtg { font-size: .7rem; font-weight: 700; color: var(--muted); }
.hcp .craft-desc { font-size: .76rem; color: var(--muted); line-height: 1.6; flex: 1; }
.hcp .hire-btn { display: flex; align-items: center; justify-content: center; gap: 7px; background: #000; color: #fff000; border: none; border-radius: 10px; padding: 10px 0; font-weight: 700; font-size: .82rem; cursor: pointer; margin-top: auto; font-family: 'Inter', sans-serif; transition: all .2s; width: 100%; }
.hcp .hire-btn:hover { background: #1a1a1a; transform: translateY(-1px); }

.hcp .locked-bar { display: flex; align-items: center; gap: 16px; background: #0a0a0a; border-radius: 14px; padding: 18px 22px; margin-bottom: 20px; flex-wrap: wrap; }
.hcp .locked-av { width: 56px; height: 56px; border-radius: 50%; object-fit: cover; border: 3px solid #fff; flex-shrink: 0; }
.hcp .locked-name { font-weight: 700; font-size: 1.15rem; color: #fff; }
.hcp .locked-meta { font-size: .76rem; color: rgba(255,255,255,.6); font-weight: 500; margin-top: 3px; }
.hcp .back-btn { display: inline-flex; align-items: center; gap: 7px; background: none; border: 1.5px solid var(--border); color: var(--muted); border-radius: 9px; padding: 8px 15px; font-size: .8rem; font-weight: 600; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .15s; margin-bottom: 18px; }
.hcp .back-btn:hover { border-color: #000; color: var(--text); background: #fffbc7; }
.hcp .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
.hcp .lbl { display: block; font-size: .72rem; font-weight: 600; color: var(--muted); margin-bottom: 6px; }
.hcp .inp, .hcp .sel, .hcp .ta { width: 100%; padding: 11px 13px; font-size: .88rem; border: 1.5px solid var(--border); border-radius: 10px; background: var(--off); color: var(--text); font-family: 'Inter', sans-serif; outline: none; transition: border-color .2s, box-shadow .2s; font-weight: 500; display: block; appearance: auto; }
.hcp .inp:focus, .hcp .sel:focus, .hcp .ta:focus { border-color: #000; box-shadow: 0 0 0 4px rgba(255,240,0,.35); background: #fff; }
.hcp .ta { resize: vertical; min-height: 95px; }
.hcp .ck-row { display: flex; align-items: center; gap: 8px; padding: 9px 0; }
.hcp .ck { width: 16px; height: 16px; accent-color: #000000; cursor: pointer; }
.hcp .ck-lbl { font-size: .85rem; font-weight: 600; color: var(--text); cursor: pointer; }
.hcp .submit-btn { width: 100%; padding: 14px; font-size: .93rem; font-weight: 700; background: #fff000; color: #000000; border: none; border-radius: 11px; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .2s; margin-top: 10px; display: flex; align-items: center; justify-content: center; gap: 8px; }
.hcp .submit-btn:hover:not(:disabled) { filter: brightness(.96); }
.hcp .submit-btn:disabled { opacity: .55; cursor: not-allowed; }
.hcp .ok-banner { background: #f0fdf4; border: 1.5px solid #bbf7d0; border-radius: 12px; padding: 13px 17px; font-size: .88rem; color: #15803d; font-weight: 600; display: flex; align-items: center; gap: 10px; margin-bottom: 20px; }

/* ── tables ── */
.hcp .tbl-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
.hcp .tbl { width: 100%; border-collapse: collapse; min-width: 700px; }
.hcp .tbl th { background: #fff; font-size: .76rem; font-weight: 500; color: #555; padding: 12px 16px; text-align: left; border-bottom: 1px solid var(--border); white-space: nowrap; }
.hcp .tbl td { padding: 14px 16px; border-bottom: 1px solid #f2f2f4; font-size: .84rem; vertical-align: middle; font-family: 'Inter', sans-serif; }
.hcp .tbl tr:last-child td { border-bottom: none; }
.hcp .tbl tbody tr:hover { background: #fafafb; }

.hcp .bdg { display: inline-flex; align-items: center; gap: 5px; border-radius: 6px; padding: 4px 10px; font-size: .7rem; font-weight: 600; white-space: nowrap; }
.hcp .bdg-g  { background: #dcfce7; color: #166534; }
.hcp .bdg-y  { background: #fff49a; color: #4d4600; }
.hcp .bdg-r  { background: #fee2e2; color: #991b1b; }
.hcp .bdg-gr { background: #f3f4f6; color: #6b7280; }
.hcp .bdg-b  { background: #eff6ff; color: #1d4ed8; }
.hcp .bdg-dot { width: 6px; height: 6px; border-radius: 50%; background: currentColor; display: inline-block; flex-shrink: 0; }

.hcp .act { padding: 5px 12px; border-radius: 8px; font-size: .72rem; font-weight: 600; cursor: pointer; border: 1.5px solid; font-family: 'Inter', sans-serif; transition: all .14s; white-space: nowrap; }
.hcp .act-ok  { background: #fff; color: #111; border-color: #cfcfd4; }
.hcp .act-ok:hover  { background: #fff000; color: #000; border-color: #fff000; }
.hcp .act-bad { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.hcp .act-bad:hover { background: #b91c1c; color: #fff; border-color: #b91c1c; }
.hcp .act-pay { background: #fff000; color: #000; border-color: #fff000; font-weight: 700; cursor: pointer; }
.hcp .act-pay:hover { filter: brightness(.95); transform: translateY(-1px); }
.hcp .act-pay:disabled { background: #e5e7eb; color: #9ca3af; border-color: #e5e7eb; cursor: not-allowed; transform: none; filter: none; }
.hcp .act-review { background: #fffbc7; color: #4d4600; border-color: #efe36a; }
.hcp .act-review:hover { background: #fff000; border-color: #fff000; }

.hcp .proof-gallery { display: flex; gap: 5px; flex-wrap: wrap; align-items: center; }
.hcp .proof-thumb { width: 40px; height: 40px; object-fit: cover; border-radius: 7px; border: 2px solid var(--border); cursor: pointer; transition: border-color .15s, transform .15s; display: block; flex-shrink: 0; }
.hcp .proof-thumb:hover { border-color: #fff000; transform: scale(1.1); }
.hcp .proof-more { width: 40px; height: 40px; border-radius: 7px; background: #f1f5f9; border: 2px solid var(--border); display: flex; align-items: center; justify-content: center; font-size: .65rem; font-weight: 800; color: #64748b; cursor: pointer; flex-shrink: 0; }
.hcp .proof-awaiting { display: inline-flex; align-items: center; gap: 5px; font-size: .72rem; color: #92400e; font-weight: 600; background: #fffbeb; border: 1.5px solid #fde68a; border-radius: 20px; padding: 3px 10px; white-space: nowrap; }
.hcp .pay-locked { display: inline-flex; align-items: center; gap: 5px; font-size: .7rem; color: #94a3b8; font-weight: 600; cursor: default; padding: 5px 10px; border: 1.5px dashed #e2e8f0; border-radius: 8px; white-space: nowrap; }

.hcp .proof-lightbox { position: fixed; inset: 0; background: rgba(0,0,0,.92); z-index: 1000; display: flex; align-items: center; justify-content: center; padding: 20px; }
.hcp .proof-close { position: absolute; top: 16px; right: 20px; background: rgba(255,255,255,.1); border: 1.5px solid rgba(255,255,255,.2); color: #fff; width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; cursor: pointer; font-size: 1rem; }

.hcp .mpesa-steps { display: flex; flex-direction: column; gap: 14px; margin: 18px 0; }
.hcp .mpesa-step { display: flex; align-items: flex-start; gap: 13px; padding: 14px; border-radius: 12px; background: var(--off); border: 1.5px solid var(--border); }
.hcp .mpesa-num { width: 28px; height: 28px; border-radius: 50%; flex-shrink: 0; background: #000000; color: #fff000; display: flex; align-items: center; justify-content: center; font-size: .72rem; font-weight: 800; }
.hcp .mpesa-step-title { font-weight: 700; font-size: .85rem; color: var(--text); margin-bottom: 2px; }
.hcp .mpesa-step-text  { font-size: .76rem; color: var(--muted); line-height: 1.6; }
.hcp .mpesa-amt { background: #0a0a0a; border-radius: 12px; padding: 16px 20px; margin: 10px 0; display: flex; justify-content: space-between; align-items: center; }
.hcp .mpesa-amt-lbl { font-size: .72rem; font-weight: 600; color: rgba(255,255,255,.5); }
.hcp .mpesa-amt-val { font-size: 1.6rem; font-weight: 800; color: #fff000; }
.hcp .mpesa-inp-wrap { margin: 16px 0; }
.hcp .mpesa-btn { width: 100%; padding: 14px; border-radius: 11px; border: none; background: linear-gradient(135deg, #16a34a, #15803d); color: #fff; font-weight: 700; font-size: .93rem; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .2s; display: flex; align-items: center; justify-content: center; gap: 8px; box-shadow: 0 6px 22px rgba(22,163,74,.3); }
.hcp .mpesa-btn:hover:not(:disabled) { filter: brightness(1.08); transform: translateY(-1px); }
.hcp .mpesa-btn:disabled { opacity: .55; cursor: not-allowed; transform: none; }
.hcp .mpesa-note { text-align: center; font-size: .74rem; color: var(--muted); margin-top: 12px; line-height: 1.6; }
.hcp .mpesa-logo-row { display: flex; align-items: center; justify-content: center; gap: 8px; margin-bottom: 16px; }
.hcp .mpesa-logo { background: #16a34a; color: #fff; border-radius: 8px; padding: 4px 12px; font-size: .78rem; font-weight: 800; letter-spacing: .05em; }

.hcp .rev-grid { display: flex; flex-direction: column; gap: 16px; }
.hcp .rev-card { background: #fff; border: 1px solid var(--border); border-radius: 14px; padding: 24px; box-shadow: 0 1px 2px rgba(16,24,40,.04), 0 6px 18px rgba(16,24,40,.04); position: relative; overflow: hidden; animation: hcp-fadeUp .3s ease both; }
.hcp .rev-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 13px; }
.hcp .rev-svc { font-weight: 700; font-size: 1.02rem; color: var(--text); margin-bottom: 2px; }
.hcp .rev-meta { font-size: .76rem; color: var(--muted); font-weight: 500; }
.hcp .rev-craft { display: flex; align-items: center; gap: 9px; margin-bottom: 14px; padding: 9px 13px; background: #f5f5f7; border-radius: 9px; }
.hcp .rev-craft-av { width: 30px; height: 30px; border-radius: 50%; border: 2px solid #fff000; object-fit: cover; }
.hcp .rev-craft-name { font-size: .82rem; font-weight: 600; color: var(--text); }
.hcp .star-row { display: flex; gap: 6px; margin-bottom: 13px; align-items: center; }
.hcp .star { font-size: 1.8rem; cursor: pointer; transition: transform .12s; color: #d1d5db; user-select: none; line-height: 1; }
.hcp .star:hover, .hcp .star.on { color: #f59e0b; transform: scale(1.2); }
.hcp .star-val { font-size: .82rem; font-weight: 700; color: var(--muted); margin-left: 4px; }
.hcp .rev-btn { background: #fff000; color: #000000; border: none; border-radius: 9px; padding: 9px 22px; font-weight: 700; font-size: .84rem; cursor: pointer; font-family: 'Inter', sans-serif; transition: all .18s; display: inline-flex; align-items: center; gap: 7px; }
.hcp .rev-btn:hover { filter: brightness(.95); }

.hcp .pay-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(175px, 1fr)); gap: 14px; margin-bottom: 20px; }
.hcp .pay-stat { background: #fff; border: 1px solid var(--border); border-radius: 14px; padding: 20px; box-shadow: 0 1px 2px rgba(16,24,40,.04); transition: transform .2s; }
.hcp .pay-stat:hover { transform: translateY(-2px); }
.hcp .pay-stat-icon { width: 42px; height: 42px; border-radius: 11px; background: #000000; display: flex; align-items: center; justify-content: center; margin-bottom: 13px; }
.hcp .pay-stat-icon i { color: #fff000; font-size: .88rem; }
.hcp .pay-stat-v { font-size: 1.55rem; font-weight: 700; color: var(--text); margin-bottom: 3px; letter-spacing: -.02em; }
.hcp .pay-stat-l { font-size: .74rem; font-weight: 500; color: var(--muted); }

.hcp .empty { text-align: center; padding: 56px 20px; }
.hcp .empty-icon { width: 66px; height: 66px; border-radius: 50%; background: var(--off); border: 1.5px solid var(--border); display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; font-size: 1.4rem; color: #cbd5e1; }
.hcp .empty h3 { font-size: .97rem; font-weight: 700; color: var(--text); margin-bottom: 4px; }
.hcp .empty p  { font-size: .83rem; color: var(--muted); }

.hcp .spinner       { width: 16px; height: 16px; border: 2.5px solid rgba(0,0,0,.18); border-top-color: #000000; border-radius: 50%; animation: hcp-spin .7s linear infinite; display: inline-block; vertical-align: middle; }
.hcp .spinner-white { width: 16px; height: 16px; border: 2.5px solid rgba(255,255,255,.25); border-top-color: #fff; border-radius: 50%; animation: hcp-spin .7s linear infinite; display: inline-block; vertical-align: middle; }
.hcp .spinner-lg    { width: 32px; height: 32px; border: 3px solid rgba(245,158,11,.2); border-top-color: #f59e0b; border-radius: 50%; animation: hcp-spin .7s linear infinite; display: block; }

.hcp .modal-bg { position: fixed; inset: 0; background: rgba(0,0,0,.6); z-index: 800; display: flex; align-items: center; justify-content: center; padding: 20px; animation: hcp-fadeUp .2s ease both; }
.hcp .modal-box { background: #fff; border-radius: 16px; border: 1px solid var(--border); font-family: 'Inter', sans-serif; box-shadow: 0 24px 80px rgba(0,0,0,.25); max-width: 560px; width: 100%; max-height: 90vh; overflow-y: auto; }
.hcp .modal-hd { border-bottom: 1px solid #f1f1f4; padding: 20px 26px; border-radius: 16px 16px 0 0; position: relative; }
.hcp .modal-hd.dark { background: #0a0a0a; }
.hcp .modal-hd.dark h5 { color: #fff; }
.hcp .modal-hd.dark p  { color: rgba(255,255,255,.5); }
.hcp .modal-hd.light { background: #fffbc7; }
.hcp .modal-hd h5 { font-weight: 700; font-size: 1.15rem; color: #0f0f10; margin-bottom: 2px; }
.hcp .modal-hd p  { font-size: .78rem; color: #64748b; }
.hcp .modal-body { padding: 24px 28px; }
.hcp .modal-ft { border-top: 1px solid #f1f1f4; padding: 16px 26px; display: flex; gap: 10px; justify-content: flex-end; flex-wrap: wrap; }
.hcp .modal-close { position: absolute; top: 16px; right: 20px; background: none; border: none; font-size: 1.1rem; cursor: pointer; color: #64748b; line-height: 1; }
.hcp .modal-hd.dark .modal-close { color: rgba(255,255,255,.5); }
.hcp .mbl { padding: 8px 18px; border-radius: 9px; font-weight: 600; font-size: .84rem; cursor: pointer; font-family: 'Inter', sans-serif; border: 1.5px solid transparent; transition: all .15s; }
.hcp .mbl-light { background: #f4f4f6; color: #555; border-color: #e5e5ea; }
.hcp .mbl-light:hover { background: #e9e9ee; }
.hcp .mbl-red   { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
.hcp .mbl-red:hover   { background: #b91c1c; color: #fff; }
.hcp .mbl-gold  { background: #fff000; color: #000000; font-weight: 700; }
.hcp .mbl-gold:hover  { filter: brightness(.95); }
.hcp .modal-hr { border: none; border-top: 1px solid #f1f1f4; margin: 16px 0; }

.hcp .proof-modal-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 10px; margin-top: 12px; }
.hcp .proof-modal-img { width: 100%; aspect-ratio: 1; object-fit: cover; border-radius: 10px; border: 2px solid var(--border); cursor: pointer; transition: border-color .15s, transform .15s; }
.hcp .proof-modal-img:hover { border-color: #fff000; transform: scale(1.04); }

.hcp .bottom-nav { display: none; position: fixed; bottom: 0; left: 0; right: 0; height: 62px; background: #000000; z-index: 300; border-top: 1px solid rgba(255,240,0,.15); align-items: stretch; padding-bottom: env(safe-area-inset-bottom, 0px); }
.hcp .bn-item { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 3px; background: none; border: none; cursor: pointer; color: rgba(255,255,255,.5); font-family: 'Inter', sans-serif; font-size: .58rem; font-weight: 600; letter-spacing: .02em; padding: 8px 4px; transition: all .15s; -webkit-tap-highlight-color: transparent; position: relative; }
.hcp .bn-item i { font-size: .95rem; line-height: 1; }
.hcp .bn-item.on { color: #fff000; }
.hcp .bn-item.on i { color: #fff000; }
.hcp .bn-item:hover { color: rgba(255,255,255,.75); }
.hcp .bn-dot { position: absolute; top: 4px; right: 26%; width: 7px; height: 7px; border-radius: 50%; background: #fff000; }

.hcp .notif-item { display: flex; gap: 12px; align-items: flex-start; padding: 14px 12px; border-radius: 12px; cursor: pointer; margin-bottom: 8px; transition: background .15s, border-color .15s; }
.hcp .notif-item:hover { filter: brightness(.98); }
.hcp .notif-icon { width: 36px; height: 36px; border-radius: 9px; background: #000; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.hcp .notif-icon i { color: #fff000; font-size: .8rem; }
.hcp .notif-title { font-weight: 600; font-size: .87rem; color: #1e293b; margin-bottom: 2px; }
.hcp .notif-msg { font-size: .78rem; color: #64748b; line-height: 1.5; }
.hcp .notif-time { font-size: .68rem; color: #94a3b8; margin-top: 4px; }
.hcp .notif-unread-dot { width: 8px; height: 8px; border-radius: 50%; background: #f59e0b; flex-shrink: 0; margin-top: 6px; }

@media (max-width: 1024px) { .hcp .craft-grid { grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 14px; } .hcp .main { padding: 24px 22px 3rem; } .hcp .dtop { padding: 0 22px; } }
@media (max-width: 992px) { .hcp .sb { display: none !important; } .hcp .dtop { display: none !important; } .hcp .topbar { display: flex !important; } .hcp .bottom-nav { display: flex !important; } .hcp .main { padding: 80px 16px 90px; } .hcp .card { padding: 1.25rem; border-radius: 14px; } .hcp .form-grid { grid-template-columns: 1fr; } .hcp .modal-bg { padding: 12px; align-items: flex-end; } .hcp .modal-box { max-height: 92vh; border-radius: 16px 16px 0 0; } }
@media (max-width: 600px) { .hcp .craft-grid { grid-template-columns: 1fr 1fr; gap: 10px; } .hcp .craft-cover { height: 110px; } .hcp .pay-grid { grid-template-columns: 1fr 1fr; } .hcp .filter-bar { flex-direction: column; gap: 8px; } .hcp .srch-wrap, .hcp .filter-sel { min-width: 100%; width: 100%; } .hcp .main { padding: 80px 12px 80px; } .hcp .card { padding: 1rem; margin-bottom: 1.25rem; } .hcp .mpesa-amt { flex-direction: column; gap: 10px; align-items: flex-start; } }
@media (max-width: 400px) { .hcp .craft-grid { grid-template-columns: 1fr; } .hcp .pay-grid { grid-template-columns: 1fr; } .hcp .main { padding: 78px 10px 76px; } }
`;

/* Every tab this page can render. Anything else (old or backend-supplied
   link_tab values like "requests" or "payments") is mapped here, so a
   notification can never land on a blank screen. */
const VALID_TABS = new Set([
  'dashboard', 'request', 'track', 'messages', 'notifications', 'invoices', 'support', 'profile',
]);
const TAB_ALIASES = {
  home: 'dashboard', saved: 'dashboard',
  browse: 'request', hire: 'request',
  requests: 'track', request_status: 'track', jobs: 'track', job: 'track', job_update: 'track',
  quote: 'track', quotes: 'track', appointment: 'track', review: 'track', reviews: 'track',
  payment: 'invoices', payments: 'invoices', invoice: 'invoices',
  message: 'messages', chat: 'messages',
  ticket: 'support', tickets: 'support', help: 'support',
};
const TYPE_TABS = { job_update: 'track', quote: 'track', review: 'track', payment: 'invoices', support: 'support', message: 'messages' };
const resolveTab = (t) => {
  const key = String(t || '').trim().toLowerCase();
  if (VALID_TABS.has(key)) return key;
  return TAB_ALIASES[key] || 'dashboard';
};

/* Light theme: banners and panels are white, black is used for text. */
const THEME = `
.hcp { --off:#f6f6f3; --border:#e6e6e1; --text:#0c0c0b; --muted:#6b6b66; font-family:'Plus Jakarta Sans','Inter',sans-serif; }
.hcp .main { background: var(--off); padding: 28px 36px 64px; }

/* sidebar stays dark; top bar and bottom nav are white */
.hcp .topbar { background:#fff; border-bottom:1px solid var(--border); }
.hcp .topbar-brand { color:#0c0c0b; }
.hcp .hamburger { background:#f3f3ef; color:#0c0c0b; border:0; }
.hcp .bottom-nav { background:#fff; border-top:1px solid var(--border); }
.hcp .bn-item { color:#6b6b66; }
.hcp .bn-item:hover, .hcp .bn-item.on, .hcp .bn-item.on i { color:#0c0c0b; }
.hcp .bn-item.on::before { content:''; position:absolute; top:0; left:30%; right:30%; height:3px; border-radius:0 0 3px 3px; background:#fff000; }
.hcp .bn-dot { background:#fff000; box-shadow:0 0 0 1.5px #0c0c0b; }

/* top bar */
.hcp .dtop { height:64px; background:#fff; border-bottom:1px solid var(--border); justify-content:space-between; }
.hcp .dtop-title { font-size:1.05rem; font-weight:700; letter-spacing:-.01em; }
.hcp .dtop-right { display:flex; align-items:center; gap:14px; }
.hcp .dtop-av { background:#fff000; color:#0c0c0b; }

/* surfaces */
.hcp .card, .hcp .pay-stat { border-radius:14px; box-shadow:none; animation:none; border-color:var(--border); }
.hcp .bdg { border-radius:6px; font-weight:600; }
.hcp .bdg-y { background:#fff7a8; color:#4d4600; }
.hcp .modal-box { border-radius:14px; }
.hcp .modal-hd.light, .hcp .modal-hd.dark { background:#fff; }
.hcp .modal-hd.dark h5 { color:#0c0c0b; }
.hcp .modal-hd.dark p { color:var(--muted); }
.hcp .modal-hd.dark .modal-close { color:var(--muted); }

/* M-Pesa modal */
.hcp .mpesa-amt { background:#fff; border:1px solid var(--border); }
.hcp .mpesa-amt-lbl { color:var(--muted); }
.hcp .mpesa-amt-val { color:#0c0c0b; }
.hcp .mpesa-num { background:#fff000; color:#0c0c0b; }
.hcp .mpesa-btn { border-radius:8px; box-shadow:none; }

/* Tracker (DispatchTrackerTab): white status panel */
.hcp .kz-status-card { background:#fff; color:#0c0c0b; border:1px solid var(--border); }
.hcp .kz-status-card.off { background:#f3f3ef; }
.hcp .kz-status-card .kz-lead { color:var(--muted); }
.hcp .kz-status-card :focus-visible { outline-color:#0c0c0b; }
.hcp .kz-status { background:#fff000; color:#0c0c0b; }
.hcp .kz-prog i { background:#e9e9e4; }
.hcp .kz-prog i.done { background:#0c0c0b; }
.hcp .kz-prog i.now { background:#fff000; box-shadow:0 0 0 2px #0c0c0b; }
.hcp .kz-prog-meta { color:var(--muted); }
.hcp .kz-prog-meta b { color:#0c0c0b; }
.hcp .kz-load { background:#e9e9e4; }
.hcp .kz-load::after { background:#0c0c0b; }
.hcp .kz-eta.done { background:#fff; box-shadow:inset 0 0 0 2px #0c0c0b; }
.hcp .kz-pin-pill { background:#fff; color:#0c0c0b; box-shadow:0 2px 10px rgba(0,0,0,.18); }
.hcp .kz-pin-pill svg { color:#0c0c0b; }
.hcp .kz-empty-ico { background:#fff; border:1px solid var(--border); color:#0c0c0b; }
.hcp .kz-photo.more { background:#f3f3ef; color:#0c0c0b; }
.hcp .kz-tag { background:#fff000; color:#0c0c0b; }
.hcp .kz-stars button.on { background:#fff000; color:#0c0c0b; }
.hcp .kz-paid .kz-round { background:#fff000; color:#0c0c0b; }

@media (max-width: 992px) { .hcp .main { padding: 80px 16px 90px; } }
`;

function Lightbox({ proofs, startIdx, onClose }) {
  const [idx, setIdx] = useState(startIdx);
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setIdx(i => (i+1) % proofs.length);
      if (e.key === 'ArrowLeft')  setIdx(i => (i-1+proofs.length) % proofs.length);
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [proofs.length, onClose]);

  const raw  = proofs[idx];
  const url  = raw?.image_url || raw?.image || (typeof raw === 'string' ? raw : null);
  const full = url ? (url.startsWith('http') ? url : `${MEDIA_BASE}${url.startsWith('/') ? '' : '/'}${url}`) : null;

  return (
    <div className="hcp proof-lightbox" onClick={onClose}>
      <button className="proof-close" onClick={onClose}>✕</button>
      <div onClick={e => e.stopPropagation()} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:14 }}>
        <img src={full} alt="Work proof"
          style={{ maxWidth:'90vw', maxHeight:'80vh', borderRadius:12, objectFit:'contain' }}
          onError={e => { e.target.src='https://placehold.co/400x300/1e293b/64748b?text=Image+not+found'; }}
        />
        <div style={{ display:'flex', alignItems:'center', gap:16 }}>
          {proofs.length > 1 && (
            <button onClick={() => setIdx(i => (i-1+proofs.length)%proofs.length)}
              style={{ background:'rgba(255,255,255,.1)', border:'1.5px solid rgba(255,255,255,.2)', color:'#fff', borderRadius:9, padding:'6px 14px', cursor:'pointer', fontSize:'.82rem', fontWeight:700 }}>‹ Prev</button>
          )}
          <span style={{ color:'rgba(255,255,255,.6)', fontSize:'.78rem' }}>{idx+1} / {proofs.length}</span>
          {proofs.length > 1 && (
            <button onClick={() => setIdx(i => (i+1)%proofs.length)}
              style={{ background:'rgba(255,255,255,.1)', border:'1.5px solid rgba(255,255,255,.2)', color:'#fff', borderRadius:9, padding:'6px 14px', cursor:'pointer', fontSize:'.82rem', fontWeight:700 }}>Next ›</button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function HireCraftsmanPage() {
  const navigate = useNavigate();
  const [tab, setTabRaw]     = useState('dashboard');
  const setTab = (t) => setTabRaw(resolveTab(t));
  const [sbOpen, setSbOpen]       = useState(false);
  const [menuOpen, setMenuOpen]   = useState(false);
  const [client, setClient]       = useState(null);
  const [jobs, setJobs]           = useState([]);
  const [craftsmen, setCraftsmen] = useState([]);
  const [quoteJob, setQuoteJob]   = useState(null);
  const [payJob,   setPayJob]     = useState(null);
  const [payPhone, setPayPhone]   = useState('');
  const [payBusy,  setPayBusy]    = useState(false);
  const [payState, setPayState]   = useState('idle');
  const [payMsg,   setPayMsg]     = useState('');
  const [lightbox, setLightbox]   = useState(null);
  const [preselected, setPreselected] = useState(null);
  const pollRef = useRef(null);
  const menuRef = useRef(null);

  // ── Notifications ──
  const [notifications, setNotifications] = useState([]);
  const [notifLoading, setNotifLoading]   = useState(false);
  const [notifError, setNotifError]     = useState('');

  // ── Support tickets ──
  const [tickets, setTickets]         = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketForm, setTicketForm]   = useState({ type:'Help', subject:'', message:'' });
  const [ticketBusy, setTicketBusy]   = useState(false);
  const [ticketOk, setTicketOk]       = useState(false);

  // ── Dispatch flow ──
  const [trackJobId, setTrackJobId] = useState(null);
  const [reqBusy, setReqBusy]       = useState(false);

  useEffect(() => {
    const id = 'kaakazini-hcp-css';
    if (!document.getElementById(id)) {
      const tag = document.createElement('style');
      tag.id = id;
      tag.textContent = CSS + (AVAILABILITY_CSS || '') + THEME;
      document.head.insertBefore(tag, document.head.firstChild);
    }
    return () => { const el = document.getElementById(id); if (el) el.remove(); };
  }, []);

  useEffect(() => () => clearInterval(pollRef.current), []);

  // Lock body scroll while the mobile drawer is open
  useEffect(() => {
    document.body.style.overflow = sbOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [sbOpen]);

  // Close the account menu on outside click / Escape
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDown = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    const onKey  = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [menuOpen]);

  useEffect(() => {
    api.get('/me/').then(r => {
      setClient(r.data);
      fetchJobs(r.data.id);
      setPayPhone(r.data.phone || r.data.phone_number || '');
    }).catch(() => setClient(null));
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/public-craftsman/`)
      .then(r => r.json())
      .then(d => setCraftsmen((d || []).filter(c =>
        c.primary_service &&
        (c.is_approved === true || c.status === 'approved' || c.is_active === true || !c.status)
      )))
      .catch(() => {});
  }, []);

  // Fetch notifications once the client is known, so the sidebar/bottom-nav
  // badge is accurate even before the Notifications tab is opened.
  useEffect(() => {
    if (client?.id) fetchNotifications();
  }, [client]);

  // Lazily load saved craftsmen / support tickets the first time each tab
  // is opened, so we don't fire extra requests nobody asked for.
  useEffect(() => {
    if (tab === 'support' && client?.id) fetchTickets();
  }, [tab, client]);

  useEffect(() => {
    if (!client?.id) return undefined;
    const timer = setInterval(() => fetchNotifications({ silent: true }), 30000);
    return () => clearInterval(timer);
  }, [client]);

  const fetchJobs = async (id) => {
    try {
      const { data } = await api.get('/job-requests/?role=client');
      const mine = (data || []).filter(j => {
        if (j.client === undefined || j.client === null) return true;
        if (typeof j.client === 'number') return j.client === id;
        if (j.client?.id !== undefined)   return j.client.id === id;
        if (j.client_id !== undefined)    return j.client_id === id;
        return true;
      });
      setJobs(mine);
    } catch (err) { console.error('fetchJobs:', err); }
  };

  // ── Notifications handlers ──
  const fetchNotifications = async ({ silent = false } = {}) => {
    if (!silent) setNotifLoading(true);
    try {
      const { data } = await api.get('/notifications/');
      setNotifications(Array.isArray(data) ? data : (data?.results || []));
      setNotifError('');
    } catch (err) {
      console.error('fetchNotifications:', err);
      if (!silent) setNotifError("We couldn't load your notifications.");
    } finally {
      if (!silent) setNotifLoading(false);
    }
  };

  const markNotificationRead = async (id) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read:true } : n));
    try { await api.post(`/notifications/${id}/read/`); } catch {}
  };

  const markAllNotificationsRead = async () => {
    setNotifications(prev => prev.map(n => ({ ...n, is_read:true })));
    try { await api.post('/notifications/mark-all-read/'); } catch {}
  };

  // Opens whatever the notification is about. A job notification opens that
  // exact job in the tracker; anything unknown falls back to the dashboard.
  const handleNotificationClick = (n) => {
    if (!n.is_read) markNotificationRead(n.id);
    const target = resolveTab(n.link_tab || TYPE_TABS[n.type]);
    if (target === 'track' && n.related_job) setTrackJobId(n.related_job);
    setPreselected(null);
    setTab(target);
    setSbOpen(false);
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  // ── Support ticket handlers ──
  const fetchTickets = async () => {
    setTicketsLoading(true);
    try {
      const { data } = await api.get('/my-tickets/');
      setTickets(data || []);
    } catch (err) { console.error('fetchTickets:', err); }
    finally { setTicketsLoading(false); }
  };

  const ticketFormChange = (e) => {
    const { id, value } = e.target;
    setTicketForm(p => ({ ...p, [id]: value }));
  };

  const submitTicket = async (e) => {
    e.preventDefault();
    if (!ticketForm.subject.trim() || !ticketForm.message.trim()) {
      alert('Please fill in subject and message.'); return;
    }
    setTicketBusy(true);
    try {
      await api.post('/raise-ticket/', {
        type: ticketForm.type,
        subject: ticketForm.subject.trim(),
        message: ticketForm.message.trim(),
      });
      setTicketOk(true);
      setTicketForm({ type:'Help', subject:'', message:'' });
      await fetchTickets();
      setTimeout(() => setTicketOk(false), 3000);
    } catch { alert('Failed to submit ticket. Please try again.'); }
    finally { setTicketBusy(false); }
  };

  // "Book again" opens the request form with this craftsman preselected.
  const openHire = (c) => {
    setPreselected(c || null);
    setTab('request');
    setSbOpen(false);
  };

  // Normal navigation clears any preselected craftsman.
  const navTo = (t) => {
    const next = resolveTab(t);
    if (next === tab) return;
    setPreselected(null);
    setTab(next);
  };

  const handleLogout = () => {
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
    } catch {}
    navigate('/login');
  };

  // Saves edits made in the Profile tab. Assumes PATCH /me/ accepts the
  // same shape GET /me/ returns (full_name, email, phone). Adjust the
  // endpoint here if the backend exposes a dedicated /client/ route instead.
  const handleProfileSave = async (updates) => {
    try {
      // send both spellings: the account serializer uses phone_number, the form uses phone
      const payload = { ...updates, ...(updates.phone ? { phone_number: updates.phone } : {}) };
      const { data } = await api.patch('/me/', payload);
      setClient(prev => ({ ...prev, ...updates, ...(data && typeof data === 'object' ? data : {}) }));
      if (updates.phone) setPayPhone(updates.phone);
    } catch (err) {
      const raw = err.response?.data?.detail || Object.values(err.response?.data || {})[0];
      const msg = Array.isArray(raw) ? raw[0] : raw;
      throw new Error(msg || 'Could not save changes. Please try again.');
    }
  };

  const quoteDecide = async (jobId, decision) => {
    if (!window.confirm(`${decision === 'approve' ? 'Approve' : 'Reject'} this quote?`)) return;
    try {
      await api.post(`/job-requests/${jobId}/quote-decision/`, { decision });
      await fetchJobs(client.id);
      setQuoteJob(null);
    } catch { alert(`Could not ${decision} quote.`); }
  };

  // Used by the tracker's Cancel button.
  const handleCancelAppointment = async (jobId) => {
    // Optimistic-only entries (not yet persisted to the backend) have
    // nothing to cancel remotely.
    if (String(jobId).startsWith('local-')) return;
    await api.patch(`/job-requests/${jobId}/`, { status: 'Cancelled' });
    await fetchJobs(client.id);
  };

  const handleMessageCraftsman = () => {
    setTab('messages');
    setSbOpen(false);
  };

  // ── Dispatch flow handlers ──
  const handleRequestService = async (fd) => {
    if (!client?.id) throw new Error('Please log in again.');
    setReqBusy(true);
    try {
      fd.append('client', client.id);
      fd.append('name',   client.full_name || '');
      fd.append('phone',  client.phone || client.phone_number || '');
      fd.append('status', 'Pending');
      fd.append('dispatch_mode', 'auto');
      const { data } = await api.post('/job-requests/', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      await fetchJobs(client.id);
      setTrackJobId(data?.id ?? null);
      setPreselected(null);
      setTab('track');
    } catch (err) {
      const raw = err.response?.data?.detail || Object.values(err.response?.data || {})[0];
      throw new Error((Array.isArray(raw) ? raw[0] : raw) || 'Could not send your request.');
    } finally { setReqBusy(false); }
  };

  const handleRate = async (job, rating, comment) => {
    const craftsmanId = job?.craftsman?.id || job?.craftsman_id;
    if (!craftsmanId) throw new Error('Missing craftsman info.');
    await api.post('/reviews/', { rating, comment, location: job.location || '', craftsman: craftsmanId });
    await fetchJobs(client.id);
  };

  const trackCount = jobs.filter(j => !/paid|cancel|closed/i.test(j.status || '')).length;
  const initiateMpesa = async () => {
    const clean = payPhone.replace(/\D/g, '');
    if (!clean.match(/^(2547|2541|07|01)\d{7,8}$/)) {
      alert('Enter a valid M-Pesa number, e.g. 0712345678 or 254712345678'); return;
    }
    clearInterval(pollRef.current);
    setPayBusy(true); setPayState('idle'); setPayMsg('');
    try {
      await api.post(`/job-requests/${payJob.id}/pay/`, { phone: clean, amount: Number(payJob.budget) });
      setPayBusy(false); setPayState('stk_sent');
      setPayMsg('Check your phone and enter your M-Pesa PIN.');
      let attempts = 0;
      pollRef.current = setInterval(async () => {
        attempts++;
        setPayState('polling');
        try {
          const { data } = await api.get(`/job-requests/${payJob.id}/pay-status/`);
          const s = (data.payment_status || '').toUpperCase();
          if (s === 'COMPLETE') {
            clearInterval(pollRef.current); setPayState('complete');
            setPayMsg('Payment confirmed! The craftsman has been notified.');
            await fetchJobs(client.id);
            setTimeout(() => { setPayJob(null); setPayState('idle'); }, 3500);
          } else if (s === 'FAIL' || s === 'FAILED') {
            clearInterval(pollRef.current); setPayState('failed');
            setPayMsg(data.failure_reason || 'Payment was not completed. Please try again.');
            await fetchJobs(client.id);
          } else if (attempts >= 30) {
            clearInterval(pollRef.current); setPayState('failed');
            setPayMsg('Status unknown — check your M-Pesa messages and refresh the page.');
          }
        } catch {}
      }, 2000);
    } catch (err) {
      setPayBusy(false); setPayState('failed');
      setPayMsg(err.response?.data?.detail || 'Payment initiation failed. Please try again.');
    }
  };

  if (!client) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'#fff', fontFamily:'Inter,sans-serif' }}>
      <div style={{ textAlign:'center' }}>
        <div style={{ width:44, height:44, border:'3px solid #ececee', borderTopColor:'#0c0c0b', borderRadius:'50%', animation:'hcp-spin .7s linear infinite', margin:'0 auto 14px' }}/>
        <p style={{ color:'#6b6b66', fontWeight:600, fontSize:'.88rem' }}>Loading your dashboard…</p>
      </div>
    </div>
  );

  const sidebarBadges = { requests: trackCount, notifications: unreadCount };

  return (
    <div className="hcp">

      {lightbox && (
        <Lightbox proofs={lightbox.proofs} startIdx={lightbox.idx} onClose={() => setLightbox(null)}/>
      )}

      {/* ── MOBILE TOPBAR ── */}
      <div className="topbar">
        <button className="hamburger" onClick={() => setSbOpen(true)} aria-label="Open menu">
          <i className="fas fa-bars"/>
        </button>
        <span className="topbar-brand"><LogoMark size={26}/>KaaKazini</span>
        <img src={avi(client.full_name)} alt={client.full_name}
          style={{ width:32, height:32, borderRadius:'50%', border:'2px solid rgba(255,240,0,.4)', objectFit:'cover', display:'block' }}/>
      </div>

      {/* ── MOBILE DRAWER + OVERLAY ── */}
      <div className={`overlay${sbOpen ? ' show' : ''}`} onClick={() => setSbOpen(false)}/>
      <nav className={`drawer${sbOpen ? ' open' : ''}`} aria-label="Mobile navigation">
        <button className="drawer-close" onClick={() => setSbOpen(false)} aria-label="Close menu">
          <i className="fas fa-times"/>
        </button>
        <HireSidebar
          client={client}
          tab={tab}
          setTab={navTo}
          onClose={() => setSbOpen(false)}
          onLogout={handleLogout}
          badges={sidebarBadges}
        />
      </nav>

      <div className="shell">
        <nav className="sb" aria-label="Navigation">
          <div className="sb-sticky">
            <HireSidebar
              client={client}
              tab={tab}
              setTab={navTo}
              onLogout={handleLogout}
              badges={sidebarBadges}
            />
          </div>
        </nav>

        <div className="col">

          {/* ── DESKTOP TOP BAR ── */}
          <header className="dtop">
            <h1 className="dtop-title">{TITLES[tab] || 'Dashboard'}</h1>
            <div className="dtop-right">
              <button className="dtop-bell" onClick={() => setTab('notifications')} aria-label="Notifications">
                <i className="fas fa-bell"/>
                {unreadCount > 0 && <span className="dtop-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>}
              </button>
              <div className="dtop-userwrap" ref={menuRef}>
                <button className="dtop-user" onClick={() => setMenuOpen(o => !o)} aria-haspopup="menu" aria-expanded={menuOpen}>
                  <span className="dtop-av">{initials(client.full_name)}</span>
                  <span className="dtop-name">{client.full_name}</span>
                  <i className="fas fa-chevron-down" style={{ fontSize:'.65rem', color:'#6b6b66' }}/>
                </button>
                {menuOpen && (
                  <div className="dtop-menu" role="menu">
                    <button role="menuitem" onClick={() => { setTab('profile'); setMenuOpen(false); }}><i className="fas fa-user"/>Profile</button>
                    <button role="menuitem" onClick={() => { setTab('support'); setMenuOpen(false); }}><i className="fas fa-circle-question"/>Support</button>
                    <hr/>
                    <button role="menuitem" className="out" onClick={handleLogout}><i className="fas fa-right-from-bracket"/>Log out</button>
                  </div>
                )}
              </div>
            </div>
          </header>

        <main className="main">

          {/* ══ DASHBOARD ══ */}
          {tab === 'dashboard' && (
            <ClientDashboardTab
              client={client}
              jobs={jobs}
              onNavigate={navTo}
              onOpenJob={(job) => { setTrackJobId(job.id); setTab('track'); }}
              onHireAgain={openHire}
            />
          )}

          {/* ══ REQUEST A SERVICE ══ */}
          {tab === 'request' && (
            <RequestServiceTab
              key={preselected?.id ?? 'new'}
              services={SERVICES}
              locations={LOCATIONS}
              craftsmen={craftsmen}
              mediaBase={MEDIA_BASE}
              preselected={preselected}
              busy={reqBusy}
              onSubmit={handleRequestService}
            />
          )}

          {/* ══ TRACK ══ */}
          {tab === 'track' && (
            <DispatchTrackerTab
              jobs={jobs}
              selectedId={trackJobId}
              onSelect={setTrackJobId}
              onRefresh={() => fetchJobs(client.id)}
              onPay={(job) => { setPayJob(job); setPayState('idle'); setPayMsg(''); }}
              onRate={handleRate}
              onCancel={handleCancelAppointment}
              onRequest={() => setTab('request')}
              onViewProof={(p, i) => setLightbox({ proofs: p, idx: i })}
              onQuoteDecide={quoteDecide}
              onViewQuote={setQuoteJob}
              onBack={() => setTab('dashboard')}
              onMessage={handleMessageCraftsman}
              mediaUrl={imgUrl}
            />
          )}

          {/* ══ MESSAGES ══ */}
          {tab === 'messages' && (
            <MessagesTab />
          )}

          {/* ══ NOTIFICATIONS ══ */}
          {tab === 'notifications' && (
            <NotificationsTab
              notifications={notifications}
              loading={notifLoading}
              error={notifError}
              onRetry={() => fetchNotifications()}
              unreadCount={unreadCount}
              onMarkAllRead={markAllNotificationsRead}
              onNotificationClick={handleNotificationClick}
            />
          )}

          {/* ══ INVOICES ══ */}
          {tab === 'invoices' && (
            <InvoicesTab jobs={jobs} clientName={client.full_name} />
          )}

          {/* ══ SUPPORT ══ */}
          {tab === 'support' && (
            <SupportTab
              ticketForm={ticketForm}
              onFormChange={ticketFormChange}
              onSubmit={submitTicket}
              busy={ticketBusy}
              ok={ticketOk}
              tickets={tickets}
              ticketsLoading={ticketsLoading}
            />
          )}

          {/* ══ PROFILE ══ */}
          {tab === 'profile' && (
            <ProfileTab client={client} jobs={jobs} onSave={handleProfileSave} />
          )}

        </main>
        </div>
      </div>

      {/* ── BOTTOM NAV ── */}
      <nav className="bottom-nav" aria-label="Navigation">
        {[
          { id:'dashboard', label:'Home', icon:'fas fa-home' },
          { id:'request', label:'Request', icon:'fas fa-bolt' },
          { id:'track', label:'My jobs', icon:'fas fa-route', badge: trackCount },
          { id:'notifications', label:'Alerts', icon:'fas fa-bell', badge: unreadCount },
          { id:'profile',  label:'Profile',  icon:'fas fa-user' },
        ].map(t => (
          <button key={t.id}
            className={`bn-item${tab === t.id ? ' on' : ''}`}
            onClick={() => setTab(t.id)} style={{ position:'relative' }}>
            <i className={t.icon}/>
            <span>{t.label}</span>
            {t.badge > 0 && <span className="bn-dot"/>}
          </button>
        ))}
      </nav>

      {/* ── QUOTE MODAL ── */}
      {quoteJob && (
        <div className="modal-bg" onClick={e => { if (e.target === e.currentTarget) setQuoteJob(null); }}>
          <div className="modal-box">
            <div className="modal-hd light" style={{ position:'relative' }}>
              <button className="modal-close" onClick={() => setQuoteJob(null)}>✕</button>
              <h5>Quote Summary</h5>
              {getJobCraftsmanName(quoteJob) !== '—' && <p>From {getJobCraftsmanName(quoteJob)}</p>}
            </div>
            <div className="modal-body">
              {quoteJob.quote_details ? (
                <>
                  {['plumberName','workType','duration','paymentTerms'].map(k =>
                    quoteJob.quote_details[k] && (
                      <div key={k} style={{ display:'flex', gap:14, marginBottom:10, fontSize:'.88rem' }}>
                        <span style={{ fontWeight:600, minWidth:125, color:'#64748b', textTransform:'capitalize' }}>
                          {k==='plumberName'?'From':k==='workType'?'Work type':k==='paymentTerms'?'Payment terms':'Duration'}
                        </span>
                        <span style={{ fontWeight:600 }}>{quoteJob.quote_details[k]}</span>
                      </div>
                    )
                  )}
                  {Array.isArray(quoteJob.quote_details.items) && quoteJob.quote_details.items.length > 0 && (
                    <>
                      <hr className="modal-hr"/>
                      <p style={{ fontWeight:600, fontSize:'.76rem', color:'#64748b', marginBottom:12 }}>Line items</p>
                      {quoteJob.quote_details.items.map((item,i) => (
                        <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:9, fontSize:'.88rem', padding:'9px 0', borderBottom:'1px solid #f1f5f9' }}>
                          <span>{item.desc||`Item ${i+1}`}</span>
                          <strong>KSh {(item.qty*item.price).toLocaleString()}</strong>
                        </div>
                      ))}
                    </>
                  )}
                  {quoteJob.quote_details.total && (
                    <>
                      <hr className="modal-hr"/>
                      <div style={{ display:'flex', justifyContent:'space-between', fontWeight:700, fontSize:'1.02rem' }}>
                        <span>Total</span>
                        <span style={{ color:'#15803d' }}>KSh {Number(quoteJob.quote_details.total).toLocaleString()}</span>
                      </div>
                    </>
                  )}
                </>
              ) : <p style={{ color:'#64748b' }}>No quote details available.</p>}
            </div>
            <div className="modal-ft">
              <button className="mbl mbl-light" onClick={() => setQuoteJob(null)}>Close</button>
              {jobs.find(j => j.id === quoteJob.id)?.status === 'Quote Submitted' && (
                <>
                  <button className="mbl mbl-red" onClick={() => quoteDecide(quoteJob.id,'reject')}>Reject Quote</button>
                  <button className="mbl mbl-gold" onClick={() => quoteDecide(quoteJob.id,'approve')}>Approve Quote</button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── M-PESA MODAL ── */}
      {payJob && (
        <div className="modal-bg" onClick={e => { if (e.target === e.currentTarget) { clearInterval(pollRef.current); setPayJob(null); setPayState('idle'); } }}>
          <div className="modal-box">
            <div className="modal-hd light" style={{ position:'relative' }}>
              <button className="modal-close" onClick={() => { clearInterval(pollRef.current); setPayJob(null); setPayState('idle'); }}>✕</button>
              <h5>Pay via M-Pesa</h5>
              <p>Job: {payJob.service} · {getJobCraftsmanName(payJob)}</p>
            </div>
            <div className="modal-body">

              {(payJob.proof_images || []).length > 0 && (
                <div style={{ marginBottom:18 }}>
                  <p style={{ fontSize:'.76rem', fontWeight:600, color:'#64748b', marginBottom:8 }}>
                    <i className="fas fa-camera" style={{ marginRight:5, color:'#16a34a' }}/>
                    Work photos ({payJob.proof_images.length})
                  </p>
                  <div className="proof-modal-grid">
                    {payJob.proof_images.map((img, i) => {
                      const url  = img.image_url || img.image || (typeof img === 'string' ? img : null);
                      const full = url ? (url.startsWith('http') ? url : `${MEDIA_BASE}${url.startsWith('/') ? '' : '/'}${url}`) : null;
                      if (!full) return null;
                      return (
                        <img key={i} src={full} alt={`Work ${i+1}`}
                          className="proof-modal-img"
                          onClick={() => setLightbox({ proofs: payJob.proof_images, idx: i })}
                          onError={e => { e.target.style.display='none'; }}
                        />
                      );
                    })}
                  </div>
                  <p style={{ fontSize:'.7rem', color:'#64748b', marginTop:6 }}>
                    Click any photo to view full size. Review the work before paying.
                  </p>
                </div>
              )}

              {payState === 'complete' && (
                <div style={{ textAlign:'center', padding:'24px 0' }}>
                  <div style={{ width:64, height:64, borderRadius:'50%', background:'#f0fdf4', border:'2px solid #22c55e', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                    <i className="fas fa-check" style={{ fontSize:'1.7rem', color:'#16a34a' }}/>
                  </div>
                  <p style={{ fontWeight:700, fontSize:'1.1rem', color:'#1e293b', marginBottom:6 }}>Payment Confirmed!</p>
                  <p style={{ fontSize:'.85rem', color:'#64748b', lineHeight:1.7 }}>
                    KSh {Number(payJob.budget).toLocaleString()} received.<br/>Craftsman has been notified. Closing…
                  </p>
                </div>
              )}

              {payState === 'failed' && (
                <div style={{ textAlign:'center', padding:'24px 0' }}>
                  <div style={{ width:64, height:64, borderRadius:'50%', background:'#fef2f2', border:'2px solid #fca5a5', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                    <i className="fas fa-times" style={{ fontSize:'1.7rem', color:'#b91c1c' }}/>
                  </div>
                  <p style={{ fontWeight:700, fontSize:'1.05rem', color:'#1e293b', marginBottom:8 }}>Payment not completed</p>
                  <p style={{ fontSize:'.85rem', color:'#64748b', lineHeight:1.7, marginBottom:18 }}>{payMsg}</p>
                  <button className="mpesa-btn" onClick={() => setPayState('idle')} style={{ maxWidth:240, margin:'0 auto' }}>
                    <i className="fas fa-redo"/> Try again
                  </button>
                </div>
              )}

              {(payState === 'stk_sent' || payState === 'polling') && (
                <div style={{ textAlign:'center', padding:'24px 0' }}>
                  <div style={{ width:64, height:64, borderRadius:'50%', background:'#fffbeb', border:'2px solid #fde68a', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                    <span className="spinner-lg"/>
                  </div>
                  <p style={{ fontWeight:700, fontSize:'1.05rem', color:'#1e293b', marginBottom:8 }}>
                    {payState === 'stk_sent' ? 'Check your phone' : 'Confirming payment…'}
                  </p>
                  <p style={{ fontSize:'.85rem', color:'#64748b', lineHeight:1.7 }}>{payMsg || 'Enter your M-Pesa PIN when prompted.'}</p>
                  <p style={{ fontSize:'.75rem', color:'#94a3b8', marginTop:10 }}>Checking automatically every 2 seconds…</p>
                </div>
              )}

              {payState === 'idle' && (
                <>
                  <div className="mpesa-logo-row">
                    <span className="mpesa-logo">M-PESA</span>
                    <span style={{ fontSize:'.78rem', color:'#64748b', fontWeight:500 }}>Powered by Safaricom</span>
                  </div>
                  <div className="mpesa-amt">
                    <div>
                      <p className="mpesa-amt-lbl">Amount to pay</p>
                      <p className="mpesa-amt-val">KSh {Number(payJob.budget).toLocaleString()}</p>
                    </div>
                    <div style={{ textAlign:'right' }}>
                      <p className="mpesa-amt-lbl">To craftsman</p>
                      <p style={{ fontSize:'.88rem', fontWeight:600, color:'#3a3a36' }}>
                        KSh {Math.round(Number(payJob.budget)*.9).toLocaleString()}
                      </p>
                      <p style={{ fontSize:'.68rem', color:'#6b6b66', marginTop:2 }}>After 10% platform fee</p>
                    </div>
                  </div>
                  <div className="mpesa-steps">
                    {[
                      { n:1, title:'Enter your M-Pesa number', text:'Use the number registered with M-Pesa' },
                      { n:2, title:'Check your phone',         text:"You'll receive a prompt from M-Pesa" },
                      { n:3, title:'Enter your PIN',           text:'Confirm — status updates automatically here' },
                    ].map(s => (
                      <div className="mpesa-step" key={s.n}>
                        <span className="mpesa-num">{s.n}</span>
                        <div>
                          <p className="mpesa-step-title">{s.title}</p>
                          <p className="mpesa-step-text">{s.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mpesa-inp-wrap">
                    <label className="lbl" style={{ marginBottom:6, display:'block' }}>M-Pesa phone number</label>
                    <input type="tel" className="inp"
                      placeholder="0712345678 or 254712345678"
                      value={payPhone}
                      onChange={e => setPayPhone(e.target.value.replace(/\D/g,''))}
                      maxLength={12}
                    />
                  </div>
                  <button className="mpesa-btn" onClick={initiateMpesa} disabled={payBusy}>
                    {payBusy
                      ? <><span className="spinner-white"/>Sending</>
                      : <>
                          <svg width="100" height="24" viewBox="0 0 260 54" fill="none" style={{ flexShrink:0, display:'inline-block', verticalAlign:'middle' }}>
                            <text x="2" y="44" fontFamily="Inter, sans-serif" fontSize="48" fontWeight="900" fill="#ffffff">M-</text>
                            <text x="80" y="44" fontFamily="Inter, sans-serif" fontSize="48" fontWeight="900" fill="#e8ff00">PESA</text>
                          </svg>
                          Send · KSh {Number(payJob.budget).toLocaleString()}
                        </>
                    }
                  </button>
                  <p className="mpesa-note">
                    Secure payment via Safaricom M-Pesa.<br/>
                    Platform fee: KSh {Math.round(Number(payJob.budget)*.1).toLocaleString()} (10%)
                  </p>
                </>
              )}
            </div>
            <div className="modal-ft">
              <button className="mbl mbl-light" onClick={() => { clearInterval(pollRef.current); setPayJob(null); setPayState('idle'); }}>
                {payState === 'complete' ? 'Done' : 'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}